import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAPIMutation } from "@/services/api/hook";

// Persist which version a card is showing, so navigating between versions
// survives a reload or a reconnect. The card moves optimistically and reports
// the failure back through onError, which restores what the payload says.
interface SelectedVersionRequest {
  pathParams: { messageId: string };
  data: { version: number };
}

export type SelectDraftVersion = (
  messageId: string,
  version: number,
  onFailure: () => void
) => void;

export const useDraftEmailSelectedVersion = (): SelectDraftVersion => {
  const { t } = useTranslation();
  const { mutate } = useAPIMutation<SelectedVersionRequest, void>(
    "messaging/draftSelectedVersion",
    { method: "PUT" }
  );
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  // Sequence number of the latest write per card. Clicking through the version
  // navigator fires one write per click and nothing serializes them, so a late
  // failure from a superseded write must not roll the card back to a version
  // the user has already left.
  const latestWriteRef = useRef<Record<string, number>>({});

  return useCallback(
    (messageId: string, version: number, onFailure: () => void) => {
      const writeSequence = (latestWriteRef.current[messageId] ?? 0) + 1;
      latestWriteRef.current[messageId] = writeSequence;
      mutateRef.current(
        { pathParams: { messageId }, data: { version } },
        {
          onSuccess: () => {},
          onError: () => {
            if (latestWriteRef.current[messageId] !== writeSequence) return;
            onFailure();
            toast.error(t("draftEmail.versionSelectError"));
          }
        }
      );
    },
    [t]
  );
};
